'use client'
import { useWindowSize } from '@/hooks/use-window-size'
import { Fancybox, Html } from '@fancyapps/ui'
import { AnimatePresence } from 'framer-motion'
import NextImage from 'next/image'
import { useRef, useState } from 'react'
import { CommonTypeImage, ZoomableImage } from './left-side-detail-products'
import { MobileGalleryModal } from './modal-gallery-mobile'
import { getOptimizedImageUrl } from '@/utils/helpers'

const INITIAL_IMAGES_COUNT = 12 // Số ảnh ban đầu
const LOAD_MORE_COUNT = 5 // Số ảnh load thêm mỗi lần
const TRIGGER_THRESHOLD = 3 // Trigger khi còn 3 items từ cuối

const handleGenListImagesForFancybox = (images: CommonTypeImage[]) => {
  return images.map((image, index) => ({
    ...image,
    src: getOptimizedImageUrl(image.src, 1200, 90), // 1200px width, quality 90
    thumb: getOptimizedImageUrl(image.src, 1200, 90), // Thumbnail
    preload: index < 2,
  }))
}

export const RenderImage = ({
  item,
  index,
  allImages,
  handleStartAutoplay,
  handleStopAutoplay,
}: {
  item: CommonTypeImage
  index: number
  allImages: {
    withZoomable: CommonTypeImage[]
    raw: string[]
  }
  handleStartAutoplay: () => void
  handleStopAutoplay: () => void
}) => {
  const { isMobile } = useWindowSize()
  const [showMobileGallery, setShowMobileGallery] = useState(false)
  const fancyboxInstanceRef = useRef<any>(null)
  const carouselRef = useRef<any>(null)
  const loadedCountRef = useRef(INITIAL_IMAGES_COUNT)
  const isLoadingRef = useRef(false)

  const loadMoreImages = () => {
    if (isLoadingRef.current || !carouselRef.current) return

    const totalImages = allImages?.withZoomable?.length || 0
    const currentLoadedCount = loadedCountRef.current

    // Nếu đã load hết ảnh thì không load thêm
    if (currentLoadedCount >= totalImages) return

    isLoadingRef.current = true

    // Tính số ảnh cần load thêm
    const nextCount = Math.min(currentLoadedCount + LOAD_MORE_COUNT, totalImages)
    const newImages = allImages?.withZoomable?.slice(currentLoadedCount, nextCount) || []

    if (newImages.length > 0) {
      // Thêm ảnh mới vào carousel với optimization
      setTimeout(() => {
        carouselRef.current.add(handleGenListImagesForFancybox(newImages))
      }, 150)

      loadedCountRef.current = nextCount
    }

    setTimeout(() => {
      isLoadingRef.current = false
    }, 500)
  }

  // ZoomableImage trên desktop
  if (item?.zoomAble && !isMobile) {
    return (
      <ZoomableImage
        src={item?.src || ''}
        alt={`Review image ${index}`}
        isShowZoomBasic={isMobile}
        allImages={allImages?.raw || []}
        currentIndex={index}
      />
    )
  }

  const handleMobileImageClick = () => {
    if (isMobile) {
      // Mobile: Lazy loading với Fancybox
      // Tính số ảnh ban đầu: từ đầu đến index + 5, tối thiểu là INITIAL_IMAGES_COUNT
      const calculatedCount = Math.max(index + 5, INITIAL_IMAGES_COUNT)
      const initialImagesCount = Math.min(calculatedCount, allImages?.withZoomable?.length || 0)

      // Optimize ảnh trước khi truyền vào Fancybox
      const initialImages = handleGenListImagesForFancybox(
        allImages?.withZoomable?.slice(0, initialImagesCount) || []
      )
      // Reset refs
      loadedCountRef.current = initialImagesCount
      isLoadingRef.current = false

      fancyboxInstanceRef.current = Fancybox.show(initialImages, {
        startIndex: Math.min(index, initialImages.length - 1),
        infinite: false,
        on: {
          'Carousel.ready': (fancybox: any) => {
            carouselRef.current = fancybox?.getCarousel()
            handleStopAutoplay()
          },
          'Carousel.change': (fancybox: any, carousel: any) => {
            const currentIndex = carousel?.getPageIndex()
            const totalLoadedSlides = carousel?.getSlides()?.length
            const totalImages = allImages?.withZoomable?.length || 0

            // Trigger load more khi user xem item thứ 3 từ cuối
            const isNearEnd = currentIndex >= totalLoadedSlides - TRIGGER_THRESHOLD
            const hasMoreImages = loadedCountRef.current < totalImages
            if (isNearEnd && hasMoreImages && !isLoadingRef.current) {
              loadMoreImages()
            }
          },
          close: () => {
            fancyboxInstanceRef.current = null
            carouselRef.current = null
            handleStartAutoplay()
          },
        },
      } as any)
    } else {
      // Desktop: Load tất cả ảnh với optimization
      const optimizedImages = handleGenListImagesForFancybox(allImages?.withZoomable || [])
      Fancybox.show(optimizedImages, {
        startIndex: index,
      })
    }
  }

  const handleCloseGallery = () => {
    setShowMobileGallery(false)
    handleStartAutoplay()
  }

  return (
    <>
      <NextImage
        onClick={handleMobileImageClick}
        style={{ width: '100%', height: '100%', cursor: 'pointer', objectFit: 'cover' }}
        src={item?.src}
        width={isMobile ? 480 : 768}
        height={isMobile ? 480 : 768}
        loading="lazy"
        quality={isMobile ? 75 : 90}
        sizes={isMobile ? '100vw' : '50vw'}
        alt="product"
      />

      <AnimatePresence mode="wait">
        {isMobile && showMobileGallery && (
          <MobileGalleryModal
            images={allImages?.withZoomable || []}
            onClose={handleCloseGallery}
            isShow={showMobileGallery}
            initialIndex={index}
          />
        )}
      </AnimatePresence>
    </>
  )
}
